import { Box, Tab, Tabs } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getFunctionInformation,
  getTasksFromViewPlanning,
  getView,
  updateViewPlanning,
} from 'services/api';
import { PlanningForm, Task } from 'types/types';
import FormulairePlanningException from './FormulairePlanningException';
import PlanningFooter from './PlanningFooter';
import PlanningSensorList from './PlanningSensorList';
import S from './PlanningVue.module.scss';
import TableauPlanningExceptionnel from './TableauPlanningExceptionnel';
import Timeline from './Timeline';
import { Mode } from './types';
import { useCopyDay } from './useCopyDay';
import WeekHeader from './WeekHeader';

const colorsArrayElectrovanne = ['#4789BC', '#A6E5EB'];
const colorsArrayBaseVie = ['#FF5722', '#FFBF00', '#4CAF50', '#2196F3'];
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const getDatesBetweenDates = (startDate: Date, endDate: Date): Date[] => {
  let dates: Date[] = [];
  //to avoid modifying the original date
  const theDate = new Date(startDate);
  while (theDate <= endDate) {
    dates = [...dates, new Date(theDate)];
    theDate.setDate(theDate.getDate() + 1);
  }
  return dates;
};

const Planning = (props: any) => {
  const { viewId } = useParams<{ viewId: string }>();
  const [defaultModes, setDefaultModes] = useState<Mode[]>([]);
  const [selectedMode, setSelectedMode] = useState<Mode>(defaultModes[0]);
  const [currentInteractionMode, setCurrentInteractionMode] =
    useState<string>('color');
  const [markerMatrix, setMarkerMatrix] = useState<number[][]>(
    Array.from({ length: 7 }, () => [])
  );
  const [timelineMatrix, setTimelineMatrix] = useState<Mode[][]>(
    Array.from({ length: 7 }, () =>
      Array.from({ length: 48 }, () => defaultModes[0])
    )
  );
  const [exMarkerMatrix, setExMarkerMatrix] = useState<number[][]>();
  const [exTimelineMatrix, setExTimelineMatrix] = useState<Mode[][]>();
  const [savingState, setSavingState] = useState<number>(0);
  const [exceptionPlanningInfo, setExceptionPlanningInfo] = useState<{
    title: string;
    startDate: Date;
    endDate: Date;
  }>({ title: '', startDate: new Date(), endDate: new Date() });
  const {
    sourceDayForCopy,
    targetDaysForCopy,
    handleDaySelection,
    resetCopyMode,
  } = useCopyDay(
    exTimelineMatrix ? exTimelineMatrix : timelineMatrix,
    exMarkerMatrix ? exMarkerMatrix : markerMatrix
  );

  const [defaultTask, setDefaultTask] = useState<Task>({} as Task);
  const [actualView, setActualView] = useState<any>();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [tabValue, setTabValue] = React.useState(0);

  let taskSkelly: any = defaultTask;

  const handleTabNav = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0 || newValue === 1) {
      setExceptionPlanningInfo({
        title: '',
        startDate: new Date(),
        endDate: new Date(),
      });
      setExMarkerMatrix(undefined);
      setExTimelineMatrix(undefined);
    }
    setTabValue(newValue);
  };

  const toggleMarkerMode = useCallback(() => {
    setCurrentInteractionMode('add-marker');
  }, [setCurrentInteractionMode]);

  const toggleRemoveMode = useCallback(() => {
    setCurrentInteractionMode('remove-marker');
  }, [setCurrentInteractionMode]);

  const toggleCopyMode = useCallback(() => {
    if (currentInteractionMode === 'copy-day') {
      setCurrentInteractionMode('color');
      resetCopyMode();
    } else {
      setCurrentInteractionMode('copy-day');
      resetCopyMode();
    }
  }, [currentInteractionMode, resetCopyMode]);

  const fetchTasks = async () => {
    const response = await getView({
      view_id: parseInt(props.viewId ? props.viewId : viewId),
    });
    const actualView = response.data.view;
    setActualView(actualView);
    const getFunctionArguments = await getFunctionInformation({
      viewType: actualView.view_type,
    });
    const functionInfo = getFunctionArguments.data[0];
    if (functionInfo) {
      const modes = functionInfo.arguments_definition[0].options.map(
        (option: { key: string; value: string }, index: number) => {
          return {
            id: option.value,
            name: option.key,
            color:
              actualView.view_type === 5
                ? colorsArrayElectrovanne[index]
                : colorsArrayBaseVie[index],
          };
        }
      );
      setDefaultModes(modes);
      setSelectedMode(modes[0]);
      getTasksFromViewPlanning({
        viewId: parseInt(props.viewId ? props.viewId : viewId),
      })
        .then((response: any) => {
          let tasks = response.data.tasks;
          setTasks(tasks);
          setDefaultTask(
            tasks.find((task: any) => task.state === 1 || task.state === 0)
          );
          const newMarkerMatrix: number[][] = Array.from(
            { length: 7 },
            () => []
          );
          const newTimelineMatrix: Mode[][] = Array.from({ length: 7 }, () =>
            Array.from({ length: 48 }, () => modes[0])
          );
          const dayModes: { [key: string]: { [key: number]: Mode } } = {};

          tasks.forEach((task: any) => {
            if (task.state === 2 || task.state === 3) return;
            let dayIndex = JSON.parse(task.cron_day_of_week!);
            dayIndex = dayIndex === 6 ? 0 : dayIndex + 1;
            const hour = parseInt(task.cron_hour!, 10);
            const minute = parseInt(task.cron_minute!, 10);
            const marker = hour * 2 + (minute === 30 ? 1 : 0);

            if (!dayModes[dayIndex]) {
              dayModes[dayIndex] = {};
            }
            const mode =
              modes.find(
                (mode: any) =>
                  '' + mode.id ===
                  ((task.id_arguments && task.id_arguments[0]?.Value) || '')
              ) || modes[0];
            dayModes[dayIndex][marker] = mode;

            if (!newMarkerMatrix[dayIndex].includes(marker)) {
              newMarkerMatrix[dayIndex].push(marker);
            }
          });

          Object.entries(dayModes).forEach(([dayIndex, markers]) => {
            const dayIndexNum = parseInt(dayIndex);
            const sortedMarkers = Object.keys(markers)
              .map(Number)
              .sort((a, b) => a - b);

            for (let i = 0; i < 48; i++) {
              const lastMarker = sortedMarkers
                .filter((marker) => marker <= i)
                .pop();

              if (lastMarker !== undefined) {
                newTimelineMatrix[dayIndexNum][i] = markers[lastMarker];
              }
            }
          });

          setMarkerMatrix(newMarkerMatrix);
          setTimelineMatrix(newTimelineMatrix);
        })
        .catch((error) => {
          console.error('Error fetching tasks:', error);
        });
    }
  };

  useEffect(() => {
    setSavingState(2);
    fetchTasks();
    setSavingState(2);
  }, []);

  function parseDMY(value: string, separator: string): Date {
    var date = value.split(separator);
    var d = parseInt(date[0], 10),
      m = parseInt(date[1], 10),
      y = parseInt(date[2], 10);
    return new Date(y, m - 1, d);
  }

  const displayExPlanning = (e: any) => {
    const allDaysInbetween = getDatesBetweenDates(
      parseDMY(e.cron_start_day, '-'),
      parseDMY(e.cron_end_date, '-')
    );

    const markerArray = Array.from({ length: allDaysInbetween.length }, () => [
      0,
    ]);
    const timelineArray = Array.from({ length: allDaysInbetween.length }, () =>
      Array.from({ length: 48 }, () => defaultModes[0])
    );
    const dayModes: { [key: string]: { [key: number]: Mode } } = {};
    const exceptionTasks = tasks.filter(
      (task: any) =>
        task.state === 3 &&
        task.cron_start_day === e.cron_start_day &&
        task.cron_end_date === e.cron_end_date
    );
    exceptionTasks.forEach((task: any) => {
      const dayIndex = allDaysInbetween.findIndex(
        (date) =>
          date.getTime() ===
          new Date(task.cron_year, task.cron_month - 1, task.cron_day).getTime()
      );
      const hour = parseInt(task.cron_hour!, 10);
      const minute = parseInt(task.cron_minute!, 10);
      const markerValue = hour * 2 + (minute === 30 ? 1 : 0);

      if (!dayModes[dayIndex]) {
        dayModes[dayIndex] = {};
      }
      const mode =
        defaultModes.find(
          (mode: any) =>
            mode.id ===
            ((task.id_arguments && task.id_arguments[0]?.Value) || '')
        ) || defaultModes[0];
      dayModes[dayIndex][markerValue] = mode;
      if (!markerArray[dayIndex]?.includes(markerValue)) {
        markerArray[dayIndex].push(markerValue);
      }
    });
    Object.entries(dayModes).forEach(([dayIndex, markers]) => {
      const dayIndexNum = parseInt(dayIndex);
      const sortedMarkers = Object.keys(markers)
        .map(Number)
        .sort((a, b) => a - b);
      for (let i = 0; i < 48; i++) {
        const lastMarker = sortedMarkers.filter((marker) => marker <= i).pop();

        if (lastMarker !== undefined) {
          timelineArray[dayIndexNum][i] = markers[lastMarker];
        }
      }
    });
    setExPlanningInfo({
      title: e.name,
      startDate: parseDMY(e.cron_start_day, '-'),
      endDate: parseDMY(e.cron_end_date, '-'),
    });
    setExMarkerMatrix(markerArray);
    setExTimelineMatrix(timelineArray);
    setTabValue(3);
  };

  const handleMarkerMatrix = (dayIndex: number, newMarkers: number[]) => {
    const newMatrix = exceptionPlanningInfo.title
      ? [...exMarkerMatrix!]
      : [...markerMatrix];
    const markersWithZero = newMarkers.includes(0)
      ? newMarkers
      : [0, ...newMarkers];
    newMatrix[dayIndex] = markersWithZero;
    exceptionPlanningInfo.title
      ? setExMarkerMatrix(newMatrix)
      : setMarkerMatrix(newMatrix);
    setSavingState(0);
  };

  const handleTimelineMatrix = (dayIndex: number, newTimeline: Mode[]) => {
    const newMatrix = exceptionPlanningInfo.title
      ? [...exTimelineMatrix!]
      : [...timelineMatrix];
    newMatrix[dayIndex] = newTimeline;
    exceptionPlanningInfo.title
      ? setExTimelineMatrix(newMatrix)
      : setTimelineMatrix(newMatrix);
    setSavingState(0);
  };

  const handleSave = () => {
    /* On transforme le tableau de jours pour coller à la version du backend, càd on transforme les données et on décale les jours
      avec Date de js: 0=dimanche, dans le backend: 0=lundi */
    setSavingState(1);
    const manualTasks = tasks.filter((task) => task.state === 2);
    const plannedTasks = tasks.filter(
      (task) => task.state === 0 || task.state === 1
    );
    const exceptionTasks = tasks.filter(
      (task) =>
        task.state === 3 &&
        task.cron_end_date! !==
          new Intl.DateTimeFormat('fr').format(exceptionPlanningInfo.endDate) &&
        task.cron_start_day! !==
          new Intl.DateTimeFormat('fr').format(exceptionPlanningInfo.startDate)
    );
    exceptionTasks.forEach((task) => {
      task.id_arguments![1].Value = task.sensors_list.split(',');
    });
    const payload: PlanningForm = {
      project_id: props.projectId
        ? props.projectId
        : actualView
        ? actualView.project_id
        : -1,
      tasks: [],
      viewId: defaultTask.view_id,
    };
    const tasksPlanned = markerToTask(plannedTasks, markerMatrix, false);
    if (exMarkerMatrix) {
      const taskException = markerToTask(plannedTasks, exMarkerMatrix, true);
      payload.tasks.push(...taskException);
    }
    payload.tasks.push(...tasksPlanned, ...manualTasks, ...exceptionTasks);

    const requestTime = new Date();
    try {
      updateViewPlanning(payload).then((response: any) => {
        const responseTime = new Date();
        const currentDayIndex = requestTime.getDay();
        const currentDayMarkers = markerMatrix[currentDayIndex];

        if (currentDayMarkers) {
          currentDayMarkers.forEach((marker) => {
            const markerHour = Math.trunc(marker / 2);
            const markerMinute = marker % 2 === 0 ? 0 : 30;
            const markerDate = new Date(requestTime);
            markerDate.setHours(markerHour, markerMinute, 0, 0);
          });
        }
        fetchTasks();
        setSnackMessage('Le planning a été mis à jour avec succès.');
        setOpenSnack(true);
        setSavingState(2);
      });
    } catch (error: any) {
      setSavingState(0);
      setSnackMessage('Erreur lors de la mise à jour du planning.');
      setOpenSnack(true);
      console.error('Error updating planning:', error);
    } finally {
      setExceptionPlanningInfo({
        title: '',
        startDate: new Date(),
        endDate: new Date(),
      });
      setSavingState(2);
    }
  };

  const markerToTask = (
    plannedTasks: Task[],
    markerMatrix: number[][],
    isException: boolean
  ) => {
    const payload: PlanningForm = {
      project_id: props.projectId
        ? props.projectId
        : actualView
        ? actualView.project_id
        : -1,
      tasks: [],
      viewId: defaultTask.view_id,
    };
    markerMatrix.forEach((markers: number[], dayIndex: number) => {
      let dateOfTask;
      if (isException) {
        dateOfTask = new Date(
          getDatesBetweenDates(
            exceptionPlanningInfo.startDate,
            exceptionPlanningInfo.endDate
          )[dayIndex]
        );
      }
      if (!markers.includes(0)) {
        const task0 = {
          ...taskSkelly,
          name: isException ? exceptionPlanningInfo.title : taskSkelly.name,
          state: isException ? 3 : 1,
          cron_day_of_week: isException
            ? '*'
            : JSON.stringify(dayIndex === 0 ? 6 : dayIndex - 1),
          cron_day: isException ? dateOfTask!.getDate() : '*',
          cron_month: isException ? dateOfTask!.getMonth() + 1 : '*',
          cron_year: isException ? dateOfTask!.getFullYear() : '*',
          cron_start_day: isException
            ? new Intl.DateTimeFormat('fr')
                .format(exceptionPlanningInfo.startDate)
                .replaceAll('/', '-')
            : new Date().toISOString().split('T')[0],
          cron_end_date: isException
            ? new Intl.DateTimeFormat('fr')
                .format(exceptionPlanningInfo.endDate)
                .replaceAll('/', '-')
            : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                .toISOString()
                .split('T')[0],
          id_arguments: [
            {
              Position_in_arg: 1,
              Value: isException
                ? exTimelineMatrix![dayIndex][0].id
                : timelineMatrix[dayIndex][0].id,
            },
            {
              Position_in_arg: 2,
              Value: isException
                ? plannedTasks[0].sensors_list.split(',')
                : plannedTasks[0].id_arguments![1].Value,
            },
          ],
        };
        payload.tasks.push(task0);
      }
      markers.forEach((marker) => {
        let currentTask = {
          ...taskSkelly,
          name: isException ? exceptionPlanningInfo.title : taskSkelly.name,
          cron_day_of_week: isException
            ? '*'
            : JSON.stringify(dayIndex === 0 ? 6 : dayIndex - 1),
          cron_hour: JSON.stringify(Math.trunc(marker / 2)),
          cron_minute: marker % 2 === 0 ? '0' : '30',
          cron_day: isException ? dateOfTask!.getDate() : '*',
          cron_month: isException ? dateOfTask!.getMonth() + 1 : '*',
          cron_year: isException ? dateOfTask!.getFullYear() : '*',
          cron_start_day: isException
            ? new Intl.DateTimeFormat('fr')
                .format(exceptionPlanningInfo.startDate)
                .replaceAll('/', '-')
            : new Date().toISOString().split('T')[0],
          cron_end_date: isException
            ? new Intl.DateTimeFormat('fr')
                .format(exceptionPlanningInfo.endDate)
                .replaceAll('/', '-')
            : new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                .toISOString()
                .split('T')[0],
          state: isException ? 3 : 1,
          id_arguments: [
            {
              Position_in_arg: 1,
              Value: isException
                ? exTimelineMatrix![dayIndex][marker].id
                : timelineMatrix[dayIndex][marker].id,
            },

            {
              Position_in_arg: 2,
              Value: isException
                ? plannedTasks[0].sensors_list.split(',')
                : plannedTasks[0].id_arguments![1].Value,
            },
          ],
        };
        payload.tasks.push(currentTask);
      });
    });
    return payload.tasks;
  };

  const autoToManualSensorTask = (task: any) => {
    const manualTasksList: any = [];
    const tasksWithoutManual = tasks
      .filter((task) => {
        if (task.state === 2) {
          manualTasksList.push(task);
          return false;
        }
        return true;
      })
      .map((t) => {
        t.id_arguments = t.id_arguments?.map((arg: any) => {
          if (arg.Position_in_arg === 2) {
            if (arg.Value.constructor === String) {
              arg.Value = JSON.parse(arg.Value);
            }
            arg.Value = arg.Value.filter((id: string) => id !== task.devui);
          }
          return arg;
        });
        return t;
      });
    const manualTask: any = structuredClone(defaultTask);
    manualTask.state = 2;
    manualTask.id_arguments = [
      {
        Position_in_arg: 1,
        Value: task.mode,
      },
      {
        Position_in_arg: 2,
        Value: [task.devui],
      },
    ];
    const newTasks = [...tasksWithoutManual, ...manualTasksList, manualTask];
    setSavingState(0);
    setTasks(newTasks);
  };

  const manualToAutoSensorTask = (task: any) => {
    const manualTasks: any = [];
    let plannedTasks = tasks.filter((t: any) => {
      if (t.state === 2) {
        let devuiArray;
        if (t.id_arguments![1].Value.constructor === String) {
          devuiArray = JSON.parse(t.id_arguments![1].Value);
        } else {
          devuiArray = t.id_arguments![1].Value;
        }
        if (devuiArray[0] !== '' + task.devui) {
          manualTasks.push(t);
        }
        return false;
      }
      return true;
    });
    plannedTasks = plannedTasks.map((t) => {
      if (t.id_arguments![1].Value.constructor === String) {
        let devuiArray = JSON.parse(t.id_arguments![1].Value);
        devuiArray.push(task.devui);
        t.id_arguments![1].Value = devuiArray;
      } else {
        t.id_arguments![1].Value.push(task.devui);
      }
      return t;
    });
    setSavingState(0);
    setTasks([...plannedTasks, ...manualTasks]);
  };

  const handleSelect = (event: any) => {
    const { name, value } = event.target;
    const updatedTask = tasks.map((t: any) => {
      if (t.state === 2) {
        let devuiArray = t.id_arguments![1].Value;
        if (t.id_arguments![1].Value.constructor === String) {
          devuiArray = JSON.parse(t.id_arguments![1].Value);
        }
        if (devuiArray.includes(name)) {
          t.id_arguments[0].Value = value;
        }
      }
      return t;
    });
    setSavingState(0);
    setTasks(updatedTask);
  };

  const onDaySelected = useCallback(
    (dayIndex: number) => {
      const result = handleDaySelection(dayIndex, currentInteractionMode);
      if (result) {
        const { newTimelineMatrix, newMarkerMatrix } = result;
        exceptionPlanningInfo.title
          ? setExTimelineMatrix(newTimelineMatrix)
          : setTimelineMatrix(newTimelineMatrix);
        exceptionPlanningInfo.title
          ? setExMarkerMatrix(newMarkerMatrix)
          : setMarkerMatrix(newMarkerMatrix);
        setTimeout(() => {
          exceptionPlanningInfo.title
            ? setExTimelineMatrix([...newTimelineMatrix])
            : setTimelineMatrix([...newTimelineMatrix]);
          exceptionPlanningInfo.title
            ? setExMarkerMatrix([...newMarkerMatrix])
            : setMarkerMatrix([...newMarkerMatrix]);
        }, 0);
        setSavingState(0);
      }
    },
    [
      handleDaySelection,
      currentInteractionMode,
      setTimelineMatrix,
      setMarkerMatrix,
      setExMarkerMatrix,
      setExTimelineMatrix,
    ]
  );

  const renderTimelines = () => {
    if (!exceptionPlanningInfo.title) {
      return [...Array(7)].map((_, i) => {
        const dayIndex = i === 6 ? 0 : i + 1;
        const isSourceDay = sourceDayForCopy === dayIndex;
        const isTargetDay = targetDaysForCopy.includes(dayIndex);
        const isInCopyMode = currentInteractionMode === 'copy-day';

        return (
          <Timeline
            key={dayIndex}
            selectedMode={selectedMode}
            currentInteractionMode={currentInteractionMode}
            setCurrentInteractionMode={setCurrentInteractionMode}
            dayOfWeek={dayIndex}
            handleMarkerMatrix={handleMarkerMatrix}
            handleTimelineMatrix={handleTimelineMatrix}
            initialSegments={timelineMatrix[dayIndex]}
            initialMarkers={markerMatrix[dayIndex]}
            onDayClick={() => onDaySelected(dayIndex)}
            isSourceDay={isSourceDay}
            isTargetDay={isTargetDay}
            isInCopyMode={isInCopyMode}
          />
        );
      });
    } else {
      const numberOfDays = getDatesBetweenDates(
        exceptionPlanningInfo.startDate,
        exceptionPlanningInfo.endDate
      );
      return numberOfDays.map((date: Date, i: number) => {
        const isSourceDay = sourceDayForCopy === i;
        const isTargetDay = targetDaysForCopy.includes(i);
        const isInCopyMode = currentInteractionMode === 'copy-day';
        return (
          <Timeline
            key={i}
            selectedMode={selectedMode}
            currentInteractionMode={currentInteractionMode}
            setCurrentInteractionMode={setCurrentInteractionMode}
            dayOfWeek={i}
            dayDate={numberOfDays[i]}
            handleMarkerMatrix={handleMarkerMatrix}
            handleTimelineMatrix={handleTimelineMatrix}
            initialSegments={exTimelineMatrix![i]}
            initialMarkers={exMarkerMatrix![i]}
            onDayClick={() => onDaySelected(i)}
            isSourceDay={isSourceDay}
            isTargetDay={isTargetDay}
            isInCopyMode={isInCopyMode}
          />
        );
      });
    }
  };

  const handleClose = () => {
    setOpenSnack(false);
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={openSnack}
        onClose={handleClose}
        message={<span>{snackMessage}</span>}
        key={snackMessage}
        autoHideDuration={3000}
      />
    );
  };

  const setExPlanningInfo = (data: {
    title: string;
    startDate: Date;
    endDate: Date;
  }) => {
    const numberOfDays = getDatesBetweenDates(data.startDate, data.endDate);
    const marker = Array.from({ length: numberOfDays.length }, () => [0]);
    const timeline = Array.from({ length: numberOfDays.length }, () =>
      Array.from({ length: 48 }, () => defaultModes[0])
    );
    setExMarkerMatrix(marker);
    setExTimelineMatrix(timeline);
    setExceptionPlanningInfo(data);
    setTabValue(3);
  };

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const filterExceptionTasks = (allTasks: Task[]) => {
    const exceptionTasks = allTasks.filter((task) => task.state === 3);
    const uniqueTasksMap = new Map();

    exceptionTasks.forEach((task) => {
      const key = `${task.name}-${task.cron_start_day}-${task.cron_end_date}`;
      if (!uniqueTasksMap.has(key)) {
        uniqueTasksMap.set(key, task);
      }
    });

    return Array.from(uniqueTasksMap.values());
  };

  const removeExceptionPlanning = (e: any) => {
    setTasks(
      tasks.filter(
        (task) =>
          e.cron_start_day !== task.cron_start_day &&
          e.cron_end_date !== task.cron_end_date
      )
    );
    setSavingState(0);
  };

  const getUnavailableDatesPeriodes = (allTasks: Task[]): Date[] => {
    const exceptionTasks = allTasks.filter((task) => task.state === 3);

    const dates: Date[] = [];
    exceptionTasks.map((exceptionTasks) => {
      if (!exceptionTasks.cron_start_day || !exceptionTasks.cron_end_date)
        return;
      var dateStartString = exceptionTasks.cron_start_day;
      var dateEndString = exceptionTasks.cron_end_date;
      var dateStartParts = dateStartString.split('-');
      var dateEndParts = dateEndString.split('-');

      var debut = new Date(
        +dateStartParts[2],
        parseInt(dateStartParts[1]) - 1,
        +dateStartParts[0]
      );
      var fin = new Date(
        +dateEndParts[2],
        parseInt(dateEndParts[1]) - 1,
        +dateEndParts[0]
      );

      for (let d = debut; d <= fin; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
    });

    return dates;
  };

  return (
    <div className={S.container}>
      {renderSnackbar()}
      <div className={S.mainLayout}>
        <div className={`${S.rightColumn}`}>
          <PlanningSensorList
            modes={defaultModes}
            tasks={tasks}
            projectId={
              props.projectId ? props.projectId : actualView?.project_id
            }
            viewType={actualView?.view_type}
            autoToManual={autoToManualSensorTask}
            manualToAuto={manualToAutoSensorTask}
            handleSelect={handleSelect}
          />
        </div>
        <div className={S.flexGrowContainer}>
          <h3 className={S.title}>{defaultTask.name}</h3>
          <WeekHeader />

          <div className={[S.dayBox, S.modesBox, S.leftColumn].join(' ')}>
            <Tabs
              value={tabValue}
              onChange={handleTabNav}
              centered
              sx={{
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                },
              }}
            >
              <Tab label="Planning hebdomadaire" {...a11yProps(0)} />
              <Tab label="Planning d'exceptions" {...a11yProps(1)} />
            </Tabs>
            {/* Barre de contrôles horizontale avec 3 colonnes */}
            {tabValue !== 1 && tabValue !== 2 && (
              <div className={S.topControlsRow}>
                {/* Colonne 1: Sélection du mode */}
                <div className={S.modeSelectorContainer}>
                  <label>Sélectionner un mode :</label>
                  <div className={S.modeButtonsGroup}>
                    {defaultModes.length > 0 ? (
                      defaultModes.map((mode) => (
                        <button
                          key={mode.id}
                          className={`${S.modeButton} ${
                            selectedMode?.id === mode.id ? S.active : ''
                          }`}
                          style={{
                            backgroundColor:
                              selectedMode?.id === mode.id
                                ? mode.color
                                : `${mode.color}20`,
                            color:
                              selectedMode?.id === mode.id
                                ? 'white'
                                : mode.color,
                            borderColor: mode.color,
                            fontWeight:
                              selectedMode?.id === mode.id ? 600 : 500,
                          }}
                          onClick={() => {
                            setSelectedMode(mode);
                            setCurrentInteractionMode('color');
                          }}
                        >
                          {mode.name}
                        </button>
                      ))
                    ) : (
                      <>
                        <button className={S.modeButton}>Ouvert</button>
                        <button className={S.modeButton}>Fermé</button>
                      </>
                    )}
                  </div>
                </div>

                {/* Colonne 2: Ajouter/Supprimer jalons */}
                <div className={S.jalonsControls}>
                  <label>Ajouter / Supprimer des jalons :</label>
                  <div className={S.jalonsButtonsGroup}>
                    <button
                      className={`${S.jalonButton} ${
                        currentInteractionMode === 'add-marker' ? S.active : ''
                      }`}
                      onClick={toggleMarkerMode}
                      title="Ajouter des jalons"
                    >
                      <span className={S.jalonButtonIconPlus}>+</span>
                    </button>
                    <button
                      className={`${S.jalonButton} ${
                        currentInteractionMode === 'remove-marker'
                          ? S.active
                          : ''
                      }`}
                      onClick={toggleRemoveMode}
                      title="Supprimer des jalons"
                    >
                      <span className={S.jalonButtonIconMois}>-</span>
                    </button>
                  </div>
                </div>

                {/* Colonne 3: Copier sélection */}
                <button
                  className={`${S.copyButton} ${
                    currentInteractionMode === 'copy-day' ? S.active : ''
                  }`}
                  onClick={toggleCopyMode}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                  </svg>
                  Copier sélection
                </button>
              </div>
            )}
            <CustomTabPanel value={tabValue} index={0}>
              {renderTimelines()}
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={1}>
              <TableauPlanningExceptionnel
                selectioneLigne={(e: any) => displayExPlanning(e)}
                exPlannings={filterExceptionTasks(tasks)}
                onDelete={(row: any) => removeExceptionPlanning(row)}
                onView={(row: any) => displayExPlanning(row)}
              />
              <button
                onClick={(e) => handleTabNav(e, 2)}
                className={S.addExceptionButton}
              >
                Créer un nouveau planning d'exception
              </button>
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={2}>
              <FormulairePlanningException
                changerOnglet={handleTabNav}
                setExPlanningInfo={setExPlanningInfo}
                unavailableDatesPeriodes={getUnavailableDatesPeriodes(tasks)}
              />
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={3}>
              {renderTimelines()}
              {exceptionPlanningInfo.title && (
                <button
                  className={S.cancelExceptionButton}
                  onClick={() => {
                    setExceptionPlanningInfo({
                      title: '',
                      startDate: new Date(),
                      endDate: new Date(),
                    });
                    setTabValue(1);
                  }}
                >
                  Annuler
                </button>
              )}
            </CustomTabPanel>
          </div>
        </div>
      </div>
      <PlanningFooter handleClick={handleSave} savingState={savingState} />
    </div>
  );
};

export default Planning;
